/**
 * Day/Occasion Selector Component
 * 
 * Allows users to select a day (with scheduled meals) or an occasion
 * for menu planning, or create a new occasion.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/authContext';
import menuSchedulingService from '../../services/menuSchedulingService';
import { getOccasions, getOccasion } from '../../services/api';
import { createOccasionFromSchedule } from '../../services/unifiedPlanningService';

interface DayData {
  date: Date;
  dateKey: string;
  meals: any[];
}

interface Occasion {
  id: string;
  name: string;
  description?: string;
  occasion_type?: string;
  recipe_count?: number;
}

interface DayOccasionSelectorProps {
  onSelectionChange: (recipeIds: number[], source: { type: 'occasion', data: any }) => void;
  selectedSource: { type: 'occasion' | null, data: any } | null;
}

export const DayOccasionSelector: React.FC<DayOccasionSelectorProps> = ({
  onSelectionChange,
  selectedSource
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysData, setDaysData] = useState<DayData[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [creatingOccasion, setCreatingOccasion] = useState<string | null>(null); // dateKey of day being converted

  // Calculate next 14 days starting today
  const getNext14Days = () => {
    const days: DayData[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateKey = date.toISOString().split('T')[0];
      days.push({
        date,
        dateKey,
        meals: []
      });
    }
    return days;
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
    
    // Listen for occasion deletion events to refresh data
    const handleOccasionDeleted = () => {
      if (user) {
        loadData();
      }
    };
    
    window.addEventListener('occasionDeleted', handleOccasionDeleted);
    // Also refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('occasionDeleted', handleOccasionDeleted);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const token = await user.getIdToken();

      // Load days with scheduled meals
      const next14Days = getNext14Days();
      const startDate = next14Days[0].date;
      const endDate = next14Days[next14Days.length - 1].date;

      const scheduledMeals = await menuSchedulingService.getScheduledMeals(
        startDate,
        endDate,
        token
      );

      // Group meals by date
      const daysDataMap = new Map<string, DayData>();
      next14Days.forEach(day => {
        daysDataMap.set(day.dateKey, { ...day, meals: [] });
      });

      scheduledMeals.forEach((meal: any) => {
        // Handle date matching - scheduled_date might be a string or Date object
        let dateKey: string;
        if (typeof meal.scheduled_date === 'string') {
          // If it's already a date string (YYYY-MM-DD), use it directly
          if (meal.scheduled_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateKey = meal.scheduled_date;
          } else {
            // If it's a datetime string, extract just the date part
            dateKey = meal.scheduled_date.split('T')[0];
          }
        } else {
          // If it's a Date object, convert to YYYY-MM-DD
          const mealDate = new Date(meal.scheduled_date);
          dateKey = mealDate.toISOString().split('T')[0];
        }
        
        const dayData = daysDataMap.get(dateKey);
        if (dayData) {
          dayData.meals.push(meal);
        }
      });

      setDaysData(Array.from(daysDataMap.values()));

      // Load occasions
      const occasionsData: any = await getOccasions(token);
      setOccasions(occasionsData.occasions || occasionsData || []);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Failed to load days and occasions');
    } finally {
      setLoading(false);
    }
  };

  const handleDaySelect = async (dayData: DayData) => {
    if (!user) return;

    if (dayData.meals.length === 0) {
      setError('No recipes scheduled for this day');
      return;
    }

    try {
      setCreatingOccasion(dayData.dateKey);
      setError(null);
      const token = await user.getIdToken();
      
      // Automatically create occasion from this day's scheduled meals
      // Use the formatted date as the name
      const occasionName = formatDateForOccasion(dayData.date);

      // Create occasion from scheduled meals for this day
      // Check if occasion already exists for this day first
      const occasionResponse: any = await createOccasionFromSchedule(
        dayData.dateKey,
        dayData.dateKey,
        occasionName,
        token
      );

      console.log('[DayOccasionSelector] Occasion response:', {
        isExisting: occasionResponse.isExisting,
        hasOccasion: !!occasionResponse.occasion,
        occasionId: occasionResponse.occasion?.id || occasionResponse.id,
        occasionRecipesCount: occasionResponse.occasion?.recipes?.length || 0
      });

      // Extract occasion data - the response has { occasion: {...}, isExisting: true/false }
      // or { occasion: {...} } for new occasions
      const occasion = occasionResponse.occasion || occasionResponse;
      const occasionId = occasion.id;
      
      if (!occasionId) {
        throw new Error('Failed to get occasion ID from created occasion');
      }

      // The occasion object from getOccasion has a 'recipes' array
      let recipeIds = (occasion.recipes || occasion.occasion_recipes || [])
        .map((or: any) => or.recipe_id || or.id)
        .filter((id: any) => id != null);

      console.log('[DayOccasionSelector] Initial recipe IDs from occasion:', recipeIds);

      // If no recipes in the returned occasion, fetch it again
      if (recipeIds.length === 0) {
        console.log('[DayOccasionSelector] No recipes in returned occasion, fetching again...');
        const fetchedResponse: any = await getOccasion(occasionId, token);
        const fetchedOccasion = fetchedResponse.occasion || fetchedResponse;
        recipeIds = (fetchedOccasion.recipes || fetchedOccasion.occasion_recipes || [])
          .map((or: any) => or.recipe_id || or.id)
          .filter((id: any) => id != null);
        
        console.log('[DayOccasionSelector] After fetching, recipe IDs:', recipeIds);
        
        // Update occasion data with fetched data
        Object.assign(occasion, fetchedOccasion);
      }

      console.log('[DayOccasionSelector] Final recipe IDs:', recipeIds);

      if (recipeIds.length === 0) {
        console.error('[DayOccasionSelector] No recipes found. Occasion data:', occasion);
        setError('No recipes found in created occasion');
        setCreatingOccasion(null);
        return;
      }

      // Reload occasions to show the new one (or existing one)
      const occasionsData: any = await getOccasions(token);
      const allOccasions = occasionsData.occasions || occasionsData || [];
      setOccasions(allOccasions);

      // Dispatch event to notify Planning Hub if this was a new occasion
      if (!occasionResponse.isExisting) {
        window.dispatchEvent(new CustomEvent('occasionCreated', {
          detail: { occasionId: occasionId }
        }));
      }

      console.log('[DayOccasionSelector] Using occasion:', {
        id: occasionId,
        name: occasion.name,
        type: occasion.occasion_type,
        recipeCount: recipeIds.length,
        isExisting: occasionResponse.isExisting || false
      });

      // Select the newly created occasion
      onSelectionChange(recipeIds, {
        type: 'occasion',
        data: occasion
      });
    } catch (err: any) {
      console.error('Error creating occasion from day:', err);
      setError(err.message || 'Failed to create occasion from day');
    } finally {
      setCreatingOccasion(null);
    }
  };

  const formatDateForOccasion = (date: Date) => {
    // Use local date to avoid timezone issues
    const localDate = new Date(date);
    localDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone shifts
    return localDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleOccasionSelect = async (occasion: Occasion) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response: any = await getOccasion(occasion.id, token);
      
      // API returns { occasion: {...} }
      const occasionData = response.occasion || response;
      
      console.log('[DayOccasionSelector] Fetched occasion data:', {
        id: occasionData.id,
        name: occasionData.name,
        hasRecipes: !!occasionData.recipes,
        hasOccasionRecipes: !!occasionData.occasion_recipes,
        recipesLength: occasionData.recipes?.length || 0,
        occasionRecipesLength: occasionData.occasion_recipes?.length || 0
      });
      
      // Extract recipe IDs from occasion recipes
      // The occasion object has a 'recipes' array from the backend
      const recipeIds = (occasionData.recipes || occasionData.occasion_recipes || [])
        .map((or: any) => or.recipe_id || or.id)
        .filter((id: any) => id != null);

      console.log('[DayOccasionSelector] Extracted recipe IDs:', recipeIds);

      if (recipeIds.length === 0) {
        console.error('[DayOccasionSelector] No recipe IDs found. Occasion data:', occasionData);
        setError('This occasion has no recipes');
        return;
      }

      onSelectionChange(recipeIds, {
        type: 'occasion',
        data: occasionData
      });
    } catch (err: any) {
      console.error('Error selecting occasion:', err);
      setError('Failed to load occasion recipes');
    }
  };


  const formatDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayDate = new Date(date);
    dayDate.setHours(0, 0, 0, 0);

    const diffTime = dayDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    
    return dayDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isDaySelected = (dayData: DayData) => {
    return creatingOccasion === dayData.dateKey;
  };

  const isOccasionSelected = (occasion: Occasion) => {
    return selectedSource?.type === 'occasion' && 
           selectedSource?.data?.id === occasion.id;
  };

  if (loading) {
    return (
      <div className="bg-neutral-0 p-6 hairline">
        <div className="text-center font-sans text-sm text-neutral-900 opacity-60">
          Loading days and occasions...
        </div>
      </div>
    );
  }

  // Get occasion IDs that are already linked to scheduled meals
  // This helps us avoid showing duplicate entries
  const linkedOccasionIds = new Set<string>();
  daysData.forEach(day => {
    day.meals.forEach((meal: any) => {
      if (meal.occasion_id) {
        linkedOccasionIds.add(meal.occasion_id);
      }
    });
  });

    // Filter out days that are already linked to existing occasions
    // (meals on that day already have occasion_id set)
    const daysWithoutOccasion = daysData.filter(day => {
      if (day.meals.length === 0) return false;
      // Check if ALL meals on this day are linked to the same occasion
      const occasionIds = day.meals
        .map((meal: any) => meal.occasion_id)
        .filter((id: any) => id != null);
      if (occasionIds.length === 0) return true; // No meals linked to any occasion
      // If all meals are linked to the same occasion, don't show this day
      const uniqueOccasionIds = Array.from(new Set(occasionIds));
      return !(uniqueOccasionIds.length === 1 && occasionIds.length === day.meals.length);
    });

  // Filter occasions to avoid duplicates
  const seenOccasionIds = new Set<string>();
  const uniqueOccasions = occasions.filter(occasion => {
    if (seenOccasionIds.has(occasion.id)) return false;
    seenOccasionIds.add(occasion.id);
    return true;
  });

  // Combine days with meals and existing occasions into one list
  const allItems = [
    // Days with scheduled meals that aren't linked to an occasion yet
    ...daysWithoutOccasion.map(day => ({
      type: 'day' as const,
      id: day.dateKey,
      name: formatDate(day.date),
      mealCount: day.meals.length,
      data: day
    })),
    // Existing occasions (deduplicated)
    ...uniqueOccasions.map(occasion => ({
      type: 'occasion' as const,
      id: occasion.id,
      name: occasion.name,
      description: occasion.description,
      recipeCount: occasion.recipe_count || 0,
      data: occasion
    }))
  ];

  return (
    <div className="bg-neutral-0 p-4 hairline mb-4">
      {error && (
        <div className="mb-4 bg-red-600 text-white p-3 hairline font-mono text-xs uppercase tracking-wider">
          {error}
        </div>
      )}

      {allItems.length === 0 ? (
        <div className="p-4 text-center font-sans text-sm text-neutral-900 opacity-60">
          No occasions or scheduled days found. Schedule meals on the Menu Scheduling page to create occasions.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-2">
          {allItems.map((item) => {
            const isSelected = item.type === 'day' 
              ? isDaySelected(item.data as DayData)
              : isOccasionSelected(item.data as Occasion);
            
            const isCreating = item.type === 'day' && creatingOccasion === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.type === 'day') {
                    handleDaySelect(item.data as DayData);
                  } else {
                    handleOccasionSelect(item.data as Occasion);
                  }
                }}
                disabled={isCreating}
                className={`w-full text-left p-3 hairline transition-all ${
                  isSelected
                    ? 'bg-[#1b263b] text-white'
                    : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                } ${isCreating ? 'opacity-50 cursor-wait' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-sans font-medium mb-1">
                      {item.name}
                    </div>
                    {item.type === 'occasion' && (item as any).description && (
                      <div className="font-sans text-xs opacity-80 mb-1">
                        {(item as any).description}
                      </div>
                    )}
                    <div className="font-mono text-xs uppercase tracking-wider opacity-60">
                      {item.type === 'day' 
                        ? `${(item as any).mealCount} ${(item as any).mealCount === 1 ? 'meal' : 'meals'} scheduled`
                        : `${(item as any).recipeCount || 0} ${(item as any).recipeCount === 1 ? 'recipe' : 'recipes'}`
                      }
                    </div>
                  </div>
                  {isCreating ? (
                    <span className="font-mono text-xs uppercase tracking-wider opacity-80">
                      Creating...
                    </span>
                  ) : isSelected && (
                    <span className="font-mono text-xs uppercase tracking-wider opacity-80">
                      ✓
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

